import { TimeEntryService, TimerService } from "@common/client"
import {
  makeAxiosMutation,
  makeGroupPages,
  makeInfiniteQuery,
  makeNoArgsQuery,
  makeQuery,
} from "./utils"

export const timerKey = ["timer"] as const

export const timeEntriesBaseKey = ["time-entries"] as const

export const timeEntryTasksBaseKey = ["time-entry-tasks"] as const

export const taskTimeBaseKey = [...timeEntriesBaseKey, "task"] as const

export const timerQuery = makeNoArgsQuery({
  serviceCall: TimerService.getTimer,
  queryKey: timerKey,
})

const TIME_ENTRIES_PAGE_SIZE = 50

export type TimeEntryPagesParams = Omit<
  NonNullable<Parameters<typeof TimeEntryService.listTimeEntries>[0]>,
  "skip" | "limit"
>

export const timeEntriesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (params: TimeEntryPagesParams, page) =>
    TimeEntryService.listTimeEntries({ ...params, ...page }),
  queryKey: (params) => [...timeEntriesBaseKey, "pages", params],
  pageSize: TIME_ENTRIES_PAGE_SIZE,
})

export const timeEntryGroupPages = makeGroupPages({
  serviceCall: (params: TimeEntryPagesParams, page) =>
    TimeEntryService.listTimeEntries({ ...params, ...page }),
  queryKey: (params) => [...timeEntriesBaseKey, "grouped", params],
  pageSize: TIME_ENTRIES_PAGE_SIZE,
})

export const timeEntryFilterOptionsQuery = makeQuery({
  serviceCall: TimeEntryService.listTimeEntryFilterOptions,
  queryKey: (params) => [...timeEntriesBaseKey, "filter-options", params],
})

export const timeEntryTasksQuery = makeQuery({
  serviceCall: TimeEntryService.searchTimeEntryTasks,
  queryKey: (params) => [...timeEntryTasksBaseKey, "search", params],
})

export const taskTimeQuery = makeQuery({
  serviceCall: TimeEntryService.getTaskTime,
  queryKey: (params) => [...taskTimeBaseKey, params],
})

export const recentTimeEntryTasksQuery = makeNoArgsQuery({
  serviceCall: TimeEntryService.listRecentTimeEntryTasks,
  queryKey: [...timeEntryTasksBaseKey, "recent"],
})

const BOOKED_KEYS = [timerKey, timeEntriesBaseKey, timeEntryTasksBaseKey]

export const useStartTimerMutation = makeAxiosMutation({
  serviceCall: TimerService.startTimer,
  invalidates: () => [timerKey, taskTimeBaseKey],
})

export const usePauseTimerMutation = makeAxiosMutation({
  serviceCall: (_: undefined) => TimerService.pauseTimer(),
  invalidates: () => [timerKey, taskTimeBaseKey],
})

export const useResumeTimerMutation = makeAxiosMutation({
  serviceCall: (_: undefined) => TimerService.resumeTimer(),
  invalidates: () => [timerKey, taskTimeBaseKey],
})

export const useStopTimerTaskMutation = makeAxiosMutation({
  serviceCall: TimerService.stopTimerTask,
  invalidates: () => BOOKED_KEYS,
})

export const useStopTimerMutation = makeAxiosMutation({
  serviceCall: (_: undefined) => TimerService.stopTimer(),
  invalidates: () => BOOKED_KEYS,
})

export const useCreateTimeEntryMutation = makeAxiosMutation({
  serviceCall: TimeEntryService.createTimeEntry,
  invalidates: () => [timeEntriesBaseKey, timeEntryTasksBaseKey],
})

export const useDeleteTimeEntryMutation = makeAxiosMutation({
  serviceCall: TimeEntryService.deleteTimeEntry,
  invalidates: () => [timeEntriesBaseKey, timeEntryTasksBaseKey],
})
